import styles from './snackbar.module.scss'
import {
	createContext,
	useContext,
	useMemo,
	useState,
	useEffect,
	useRef,
	useCallback,
} from 'react'

interface SnackbarProps {
	autoRemove?: boolean
	autoRemoveTime?: number
	uniqueKey?: string
	snackbarData?: {
		type?: 'error' | 'success' | ''
		icon?: string | React.ReactNode
		title?: string | React.ReactNode
		desc?: string | React.ReactNode
		button?: string | React.ReactNode
	} | null
	closeAll?: boolean
	children?: React.ReactNode
	onButtonClick?: (timeStamp: string) => void
}
type SnackbarContextProps = Omit<SnackbarProps, 'children'> & {
	snackbarDataObj: {
		[key: string]: NonNullable<SnackbarProps['snackbarData']>
	} | null
	setSnackbarDataObj?: React.Dispatch<{
		[key: string]: NonNullable<SnackbarProps['snackbarData']>
	} | null>
}

const defaultContext: SnackbarContextProps = {
	autoRemove: false,
	autoRemoveTime: 3000,
	uniqueKey: '',
	closeAll: false,
	snackbarDataObj: null,
	onButtonClick: (timeStamp) => {},
}
// 이미지 업로드 컨텍스트
const SnackbarPreviewContext =
	createContext<SnackbarContextProps>(defaultContext)

export const SnackbarProvider = ({
	autoRemove = false,
	autoRemoveTime = 3000,
	uniqueKey = '',
	closeAll = false,
	snackbarData,
	onButtonClick,
}: SnackbarProps) => {
	const [snackbarDataObj, setSnackbarDataObj] = useState<{
		[key: string]: NonNullable<SnackbarProps['snackbarData']>
	} | null>(null)

	const snackbarDataArrayRef = useRef(snackbarDataObj)
	useEffect(() => {
		snackbarDataArrayRef.current = snackbarDataObj
	}, [snackbarDataObj])

	useEffect(() => {
		if (snackbarData && Object.keys(snackbarData).length > 0) {
			if (
				snackbarDataArrayRef?.current &&
				Object.keys(snackbarDataArrayRef?.current).length > 0
			) {
				const snackbarDataAddTimeStamp = {
					[String(Date.now())]: snackbarData,
				}
				setSnackbarDataObj({
					...snackbarDataArrayRef.current,
					...snackbarDataAddTimeStamp,
				})
			} else {
				setSnackbarDataObj({
					[String(Date.now())]: snackbarData,
				})
			}
		}
	}, [snackbarData])

	useEffect(() => {
		if (closeAll) {
			setSnackbarDataObj(null)
		}
	}, [closeAll])

	return (
		<SnackbarPreviewContext.Provider
			value={{
				autoRemove,
				autoRemoveTime,
				uniqueKey,
				closeAll,
				snackbarDataObj,
				setSnackbarDataObj,
				onButtonClick,
			}}
		>
			<SnackbarContainer />
		</SnackbarPreviewContext.Provider>
	)
}
export const Snackbar = ({
	timeStamp,
	data,
	onClose,
}: {
	timeStamp: string
	data?: SnackbarProps['snackbarData']
	onClose: (timeStamp: string) => void
}) => {
	const {onButtonClick} = useContext(SnackbarPreviewContext)!
	const {type, icon, title, desc, button} = data!

	const snackbarRef = useRef<HTMLDivElement | null>(null)

	const {autoRemove, autoRemoveTime} = useContext(SnackbarPreviewContext)!

	// 생성된 시점에서 자동으로 삭제
	const autoRemoveTimerRef = useRef<NodeJS.Timeout | null>(null)

	const addClassCloseAnimation = useCallback(() => {
		return new Promise((resolve, reject) => {
			if (snackbarRef.current) {
				snackbarRef.current.classList.add(styles['close'])
				setTimeout(() => {
					onClose(timeStamp)
					resolve(true)
				}, 300)
			}
		})
	}, [])

	useEffect(() => {
		if (autoRemoveTimerRef?.current) {
			clearTimeout(autoRemoveTimerRef.current)
		}
		if (!autoRemove) return
		autoRemoveTimerRef.current = setTimeout(() => {
			addClassCloseAnimation()
		}, autoRemoveTime)
		return () => {
			if (autoRemoveTimerRef?.current) {
				clearTimeout(autoRemoveTimerRef.current)
			}
		}
	}, [autoRemove])

	const closeButtonHandler = () => {
		if (!autoRemove) {
			addClassCloseAnimation()
		}
		if (onButtonClick) {
			onButtonClick(timeStamp)
		}
	}

	return (
		<>
			<div
				className={`${styles['snackbar']} ${type ? styles[type] : ''}`}
				ref={snackbarRef}
			>
				<div className={styles['snackbar-body']}>
					{icon && (
						<div className={styles['snackbar-icon']}>
							{typeof icon === 'string' ? <img src={icon} alt='' /> : icon}
						</div>
					)}
					{title && (
						<div className={styles['snackbar-title']}>
							{typeof title === 'string' ? <p>{title}</p> : title}
						</div>
					)}
					{desc && (
						<div className={styles['snackbar-content']}>
							{typeof desc === 'string' ? <p>{desc}</p> : desc}
						</div>
					)}
				</div>
				{button && (
					<div
						className={styles['snackbar-button']}
						onClick={() => closeButtonHandler()}
					>
						{typeof button === 'string' ? <button>{button}</button> : button}
					</div>
				)}
			</div>
		</>
	)
}

export const SnackbarContainer = () => {
	const {setSnackbarDataObj, snackbarDataObj} = useContext(
		SnackbarPreviewContext
	)!
	const startDataObj = useRef<{
		[key: string]: NonNullable<SnackbarProps['snackbarData']>
	} | null>(snackbarDataObj)
	useEffect(() => {
		startDataObj.current = snackbarDataObj
	}, [snackbarDataObj])

	const setSnackbarDataObjHandler = useCallback((timeStamp: string) => {
		if (
			!startDataObj?.current ||
			Object.keys(startDataObj.current).length === 0 ||
			!setSnackbarDataObj
		)
			return
		if (timeStamp in startDataObj.current) {
			delete startDataObj.current[timeStamp]
			setSnackbarDataObj({...startDataObj.current})
		}
	}, [])

	if (!snackbarDataObj || Object.keys(snackbarDataObj).length === 0) {
		return null
	}
	return (
		<div className={styles['snackbar-container']}>
			{Object.values(snackbarDataObj).map((snackbar, idx) => (
				<Snackbar
					timeStamp={Object.keys(snackbarDataObj)[idx]}
					data={snackbar}
					key={idx}
					onClose={setSnackbarDataObjHandler}
				/>
			))}
		</div>
	)
}
